import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AuditEvent,
  AuditLogPage,
  AuditLogQuery,
  AuditPagination,
  AuditResponse,
  useAuditService,
} from '../../components/services/auditService';
import { ACTIVITY_LOG_EXPORT_LIMIT, ActivityLogScope } from './activityLogFilters';

export type ActivityLogStatus = 'noTenant' | 'loading' | 'success' | 'error';

const GENERIC_ERROR = 'Could not load activity. Please try again.';
export const FORBIDDEN_ERROR = "You don't have access to this activity.";

// The shared apiService interceptor has already shown the toast and logged the failure with its correlation id;
// this only picks the text for the inline error state. 403s come from Defatom.Web.Core's ErrorHandlerMiddleware,
// whose body is PascalCase with a generic "An error occurred…" message, so they get their own text (AuditAPI
// denials: role not allowed, tenant not accessible). 400s carry the AuditAPI's readable `message`.
export const errorMessage = (error: any): string => {
  if (error?.response?.status === 403) return FORBIDDEN_ERROR;
  return error?.response?.data?.message || error?.response?.data?.title || GENERIC_ERROR;
};

/**
 * Loads the Activity log one page at a time (pageNumber/pageSize, with the API's `pagination` totals), like the
 * platform's other paged lists. New scope, tenant or filters start again at page 1; changing page keeps the current
 * rows on screen until the next page arrives. Responses that arrive after their inputs changed are ignored, so a slow
 * response for the previous tenant, filters or page can never overwrite the current list.
 */
export const useActivityLog = (scope: ActivityLogScope, tenantId: string | null, query: AuditLogQuery) => {
  const { getTenantEvents, getGlobalEvents, searchAllTenants } = useAuditService();

  const [items, setItems] = useState<AuditEvent[]>([]);
  const [pagination, setPagination] = useState<AuditPagination | null>(null);
  const [range, setRange] = useState<{ fromUtc: string; toUtc: string } | null>(null);
  const [status, setStatus] = useState<ActivityLogStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  // The page number belongs to one set of inputs, so new inputs start at page 1 without a second request.
  const inputsKey = useMemo(() => JSON.stringify([scope, tenantId, query]), [scope, tenantId, query]);
  const [page, setPage] = useState({ key: inputsKey, number: 1 });
  const pageNumber = page.key === inputsKey ? page.number : 1;

  // Bumped on every request; a response is applied only if its generation is still current.
  const generation = useRef(0);
  // Which list (inputs + reload) is on screen, and its page: a request for another page of the same list keeps the
  // rows visible, and a failed one goes back to the page still shown.
  const shownList = useRef<string | null>(null);
  const shownPage = useRef(1);

  const fetchEvents = useCallback(
    (overrides: AuditLogQuery): Promise<AuditResponse<AuditLogPage>> => {
      const request = { ...query, ...overrides };
      if (scope === 'global') return getGlobalEvents(request);
      if (scope === 'all') return searchAllTenants(request);
      return getTenantEvents(tenantId as string, request);
    },
    [scope, tenantId, query, getTenantEvents, getGlobalEvents, searchAllTenants],
  );

  useEffect(() => {
    const listKey = `${inputsKey}#${reloadToken}`;
    const samePageList = shownList.current === listKey;
    // Already on screen: e.g. the page a failed page change went back to. Keeps that failure's message visible.
    if (samePageList && pageNumber === shownPage.current) return;

    const current = ++generation.current;
    setError(null);
    setPageError(null);

    if (scope === 'tenant' && !tenantId) {
      shownList.current = null;
      setItems([]);
      setPagination(null);
      setRange(null);
      setPageLoading(false);
      setStatus('noTenant');
      return;
    }

    if (samePageList) {
      setPageLoading(true);
    } else {
      setItems([]);
      setPagination(null);
      setRange(null);
      setPageLoading(false);
      setStatus('loading');
    }

    const fail = (message: string) => {
      if (samePageList) {
        setPageError(message);
        setPage({ key: inputsKey, number: shownPage.current });
      } else {
        setError(message);
        setStatus('error');
      }
    };

    fetchEvents({ pageNumber })
      .then((response) => {
        if (current !== generation.current) return;
        if (!response?.isSuccess || !response.data) {
          fail(response?.message || GENERIC_ERROR);
          return;
        }
        shownList.current = listKey;
        shownPage.current = response.pagination?.pageNumber ?? pageNumber;
        setItems(response.data.items ?? []);
        setPagination(response.pagination ?? null);
        setRange({ fromUtc: response.data.fromUtc, toUtc: response.data.toUtc });
        setStatus('success');
      })
      .catch((err) => {
        if (current === generation.current) fail(errorMessage(err));
      })
      .finally(() => {
        if (current === generation.current) setPageLoading(false);
      });
  }, [fetchEvents, scope, tenantId, inputsKey, pageNumber, reloadToken]);

  const goToPage = useCallback((number: number) => setPage({ key: inputsKey, number }), [inputsKey]);

  // New filters or a reload start again at page 1.
  const reload = useCallback(() => {
    setPage({ key: inputsKey, number: 1 });
    setReloadToken((token) => token + 1);
  }, [inputsKey]);

  /** The newest ACTIVITY_LOG_EXPORT_LIMIT events for the same scope, tenant and filters, in one request (CSV download). */
  const fetchForExport = useCallback(
    () => fetchEvents({ pageNumber: undefined, pageSize: undefined, limit: ACTIVITY_LOG_EXPORT_LIMIT }),
    [fetchEvents],
  );

  return { items, pagination, pageNumber, range, status, error, pageLoading, pageError, goToPage, reload, fetchForExport };
};
